import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Spin, Tag } from 'antd';
import { CheckCircleFilled, SafetyOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import CBModal from './CBModal';
import { estTauxRcDuBareme } from './ChampResponsabilite';
import axiosInstance from '../variables/api/axiosInstance';
import { localeIntl } from '../i18n/format';

const GREEN = '#1a6b3c';
const ADVERSE = '#9b563a';

/* Ce que chaque taux du barème veut dire pour le dossier : la part de l'assuré est
   la part des préjudices que l'assureur prend en charge dans les offres. Titre et effet
   se lisent à l'affichage (rcAssureModal.niveaux.<cle>) : écrits ici, ils figeraient la
   langue du chargement de la page. */
const NIVEAUX = [
  { taux: 0, cle: 'nonResponsable' },
  { taux: 25, cle: 'minoritaire' },
  { taux: 50, cle: 'partagee' },
  { taux: 75, cle: 'majoritaire' },
  { taux: 100, cle: 'entiere' },
];

const pct = (n) => `${Number(n).toLocaleString(localeIntl())} %`;

/** Barre de partage : la part de l'assuré face à celle des autres parties. */
const BarrePartage = ({ part }) => {
  const { t } = useTranslation('commun');
  const p = part == null ? null : Math.max(0, Math.min(100, Number(part)));
  return (
    <div>
      <div style={{ display: 'flex', height: 10, borderRadius: 6, overflow: 'hidden', background: '#e5e7eb' }}>
        {p != null && <div style={{ width: `${p}%`, background: GREEN, transition: 'width .25s' }} />}
        {p != null && <div style={{ width: `${100 - p}%`, background: ADVERSE, opacity: 0.55, transition: 'width .25s' }} />}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 12, color: '#4b5563' }}>
        <span><span style={{ color: GREEN, fontWeight: 700 }}>{t('rcAssureModal.assure')}</span> {p != null ? pct(p) : '-'}</span>
        <span>{t('rcAssureModal.autresParties')} <span style={{ color: ADVERSE, fontWeight: 700 }}>{p != null ? pct(100 - p) : '-'}</span></span>
      </div>
    </div>
  );
};

/**
 * Établissement de la responsabilité civile de l'assuré.
 *
 * Ouvert par l'action « Établir la RC » de l'en-tête du dossier et par la carte de
 * l'assuré dans l'onglet Victimes. Le taux se choisit dans le barème par quarts, chaque
 * niveau disant ce qu'il fait aux offres.
 *
 * Quand les parties du dossier portent toutes leur part, celle de l'assuré s'en déduit
 * (100 % moins ces parts, GET /partage-responsabilite) : le modal l'explique et ne
 * propose plus de saisie, qui ne serait qu'un repli sans effet.
 */
export default function RcAssureModal({ open, onClose, sinistre, peutModifier, onEnregistre, onVoirParties }) {
  const { t } = useTranslation('commun');
  const id = sinistre?.sinistreTrackingId;
  const [partage, setPartage] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [choix, setChoix] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(() => {
    if (!id) return;
    setChargement(true);
    axiosInstance.get(`/v1/sinistres/${id}/partage-responsabilite`, { skipErrorToast: true })
      .then((r) => setPartage(r.data?.data || null))
      .catch(() => setPartage(null))
      .finally(() => setChargement(false));
  }, [id]);

  useEffect(() => { if (open) charger(); }, [open, charger]);

  // Le taux du DOSSIER est ce que l'agent a saisi ; la part par défaut (100 %) n'en est pas un.
  const tauxDossier = sinistre?.tauxRc != null ? Number(sinistre.tauxRc) : null;
  useEffect(() => {
    if (open) setChoix(estTauxRcDuBareme(tauxDossier) ? tauxDossier : null);
  }, [open, tauxDossier]);

  const deduite = partage?.origine === 'PARTIES';
  const partActuelle = partage?.partAssure != null ? Number(partage.partAssure) : tauxDossier;
  const responsables = (partage?.parties || []).filter((x) => x.taux != null && Number(x.taux) > 0);
  const sansTaux = partage?.partieAdverseSansTaux || [];
  const saisieOuverte = peutModifier && !deduite;
  const inchange = choix != null && tauxDossier != null && choix === tauxDossier;

  const nom = sinistre?.conducteurEstAssure === false && sinistre?.conducteurNom
    ? `${sinistre.conducteurNom} ${sinistre.conducteurPrenom || ''}`.trim()
    : sinistre?.assureNomComplet;

  const enregistrer = async () => {
    if (choix == null) return;
    setEnvoi(true);
    try {
      await axiosInstance.patch(`/v1/sinistres/${id}/part-assure`, null, { params: { taux: choix } });
      toast.success(t('rcAssureModal.enregistre', { taux: pct(choix) }));
      onEnregistre?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('rcAssureModal.erreur'));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <CBModal
      open={open}
      onCancel={onClose}
      width={680}
      destroyOnClose
      title={(
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <SafetyOutlined style={{ color: GREEN }} /> {t('rcAssureModal.titre')}
        </span>
      )}
      footer={saisieOuverte ? [
        <Button key="annuler" onClick={onClose}>{t('rcAssureModal.annuler')}</Button>,
        <Button
          key="ok" type="primary" loading={envoi} disabled={choix == null || inchange} onClick={enregistrer}
          style={choix == null || inchange ? undefined : { background: GREEN, borderColor: GREEN }}
        >
          {t('rcAssureModal.etablir')}
        </Button>,
      ] : [<Button key="fermer" onClick={onClose}>{t('rcAssureModal.fermer')}</Button>]}
    >
      <Spin spinning={chargement}>
        {/* Qui est concerné */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          padding: '12px 14px', borderRadius: 10, background: '#f8fafc', border: '1px solid #e5e7eb', marginBottom: 14,
        }}>
          <Tag color="geekblue" style={{ borderRadius: 20, margin: 0, fontWeight: 600 }}>{t('rcAssureModal.assure')}</Tag>
          <span style={{ fontWeight: 600 }}>{nom || t('rcAssureModal.assureDuDossier')}</span>
          {sinistre?.assureImmatriculation && <span style={{ color: '#6b7280', fontSize: 12.5 }}>{sinistre.assureImmatriculation}</span>}
          {sinistre?.numeroSinistreLocal && (
            <span style={{ marginLeft: 'auto', color: '#6b7280', fontSize: 12.5 }}>{t('rcAssureModal.dossier', { numero: sinistre.numeroSinistreLocal })}</span>
          )}
        </div>

        <p style={{ margin: '0 0 14px', color: '#4b5563', fontSize: 13 }}>
          {t('rcAssureModal.explication')}
        </p>

        {partage?.depasse100 && (
          <Alert
            type="error" showIcon style={{ marginBottom: 12 }}
            message={t('rcAssureModal.depasse100', { total: pct(partage.totalParties) })}
            description={t('rcAssureModal.depasse100Detail')}
          />
        )}

        {deduite ? (
          <Alert
            type="info" showIcon style={{ marginBottom: 14 }}
            message={t('rcAssureModal.partDeduite', { part: pct(partActuelle) })}
            description={(
              <>
                <div>
                  {t('rcAssureModal.partDeduiteDetail')}
                </div>
                {responsables.length > 0 && (
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {responsables.map((x) => (
                      <li key={x.victimeTrackingId}>
                        {x.nom}{x.adversaire ? ` ${t('rcAssureModal.partieAdverse')}` : ''} : <strong>{pct(x.taux)}</strong>
                      </li>
                    ))}
                  </ul>
                )}
                {onVoirParties && (
                  <Button type="link" style={{ padding: 0, marginTop: 4 }} onClick={() => { onClose(); onVoirParties(); }}>
                    {t('rcAssureModal.modifierParts')}
                  </Button>
                )}
              </>
            )}
          />
        ) : sansTaux.length > 0 && (
          <Alert
            type="warning" showIcon style={{ marginBottom: 14 }}
            message={t('rcAssureModal.sansTauxTitre')}
            description={t('rcAssureModal.sansTauxDetail', { parties: sansTaux.join(', ') })}
          />
        )}

        {/* Barème */}
        <div role="radiogroup" aria-label={t('rcAssureModal.baremeAria')} style={{ display: 'grid', gap: 8 }}>
          {NIVEAUX.map((n) => {
            const actif = deduite ? partActuelle === n.taux : choix === n.taux;
            const enregistre = !deduite && tauxDossier === n.taux;
            return (
              <button
                key={n.taux}
                type="button"
                role="radio"
                aria-checked={actif}
                disabled={!saisieOuverte}
                onClick={() => setChoix(n.taux)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left',
                  padding: '10px 14px', borderRadius: 10, font: 'inherit',
                  cursor: saisieOuverte ? 'pointer' : 'default',
                  border: `1.5px solid ${actif ? GREEN : '#e5e7eb'}`,
                  background: actif ? '#f0f7f2' : '#fff',
                  opacity: !saisieOuverte && !actif ? 0.55 : 1,
                  transition: 'border-color .15s, background .15s',
                }}
              >
                <span style={{
                  minWidth: 58, fontSize: 20, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                  color: actif ? GREEN : '#374151',
                }}>
                  {pct(n.taux)}
                </span>
                <span style={{ flex: 1 }}>
                  <span style={{ display: 'block', fontWeight: 600, color: '#111827' }}>{t(`rcAssureModal.niveaux.${n.cle}.titre`)}</span>
                  <span style={{ display: 'block', fontSize: 12.5, color: '#6b7280' }}>{t(`rcAssureModal.niveaux.${n.cle}.effet`)}</span>
                </span>
                {enregistre && <Tag style={{ borderRadius: 20, margin: 0 }}>{t('rcAssureModal.actuel')}</Tag>}
                {actif && <CheckCircleFilled style={{ color: GREEN, fontSize: 18 }} />}
              </button>
            );
          })}
        </div>

        {tauxDossier != null && !estTauxRcDuBareme(tauxDossier) && !deduite && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#b45309' }}>
            {t('rcAssureModal.horsBareme', { taux: pct(tauxDossier) })}
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <BarrePartage part={deduite ? partActuelle : (choix ?? partActuelle)} />
        </div>

        {!peutModifier && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#6b7280' }}>
            {t('rcAssureModal.consultationSeule')}
          </div>
        )}
      </Spin>
    </CBModal>
  );
}
